import { useState, useEffect, useContext, useLayoutEffect } from 'react';
import * as THREE from 'three';
import canvasHandler from '../utils/idGenerator.js';
import { DesignContext } from '../contexts/designContext.js';
import { counterClockWise } from '../algorithms/geometry.js';
import {
  averagePlaneAttributes,
  mapRingEdgeLabels,
  polygonArea,
  sceneFromPlanar,
  simplifyCollinearRing
} from '../algorithms/polygonEdit.js';

export const useDrawing = (
  vertices,
  verticesType,
  segments,
  obstacles,
  trees,
  treeData,
  changeClockwise,
  getInchePerSceneUnit,
  scale,
  cx,
  cy,
  c
) => {
  const {
    accordion,
    handleNewShape,
    handleSaveEdits,
    handleAddPointToPolygon,
    handleRemovePointFromPolygon,
    handleLineLabel,
    handleDeleteShape,
    handleReplaceShapes,
    annotations,
    setAnnotations
  } = useContext(DesignContext);

  const annotationList = Array.isArray(annotations) ? annotations : [];

  const pointToSegments = (v, zbased = false) => [v.x / scale + cx / c, (zbased ? v.z : v.y) / scale + cy / c];

  const verticesToSegments = (newOrgVertices, zbased = false) => {
    const newSegmentsPosition = newOrgVertices.map((p, pi) => {
      const newVertices = p.map((v) => [v.x / scale + cx / c, (zbased ? v.z : v.y) / scale + cy / c]);
      const vecs = newVertices.map((pnt) => new THREE.Vector3(pnt[0], pnt[1], 0));
      const area = THREE.ShapeUtils.area(vecs);
      if (area > 0) {
        const first = newVertices[0];
        const last = newVertices[newVertices.length - 1];
        if (first[0] === last[0] && first[1] === last[1]) {
          newVertices.pop();
        }
        // newVertices.push([...first]);
        newVertices.reverse();
        // newVertices.pop();
        // [first] = newVertices;
        // last = newVertices[newVertices.length - 1];
        // if (first[0] === last[0] && first[1] === last[1]) {
        //   newVertices.pop();
        // }
      }
      return newVertices;
    });
    return newSegmentsPosition;
  };

  const splitGeometry = (geometry) => {
    const geo = geometry.map((g) => [Math.round(g[0]), Math.round(g[1])]);
    const geos = [];
    const processed = [];
    let lastInsertedIndex = -1;
    for (let gi = 0; gi < geo.length; gi += 1) {
      const g = geo[gi];
      const pi = processed.findIndex((x) => x[0] === g[0] && x[1] === g[1]);
      if (gi < geo.length - 1 && pi !== -1 && pi > lastInsertedIndex) {
        lastInsertedIndex = gi;
        const newGeo = geo.splice(pi, gi - pi);
        if (newGeo.length > 2) {
          newGeo.push(newGeo[0]);
          geos.push(newGeo);
        }
        geos.push(...splitGeometry(geo));
        break;
      }
      processed.push(g);
    }
    if (geos.length > 0) return geos;
    if (geo.length > 2) return [geo];
    return [];
  };

  const addShape = (shape, shapeType, objectType = null) => {
    const rawType = ['Driveway', 'Wiring', 'Meter'].includes(objectType);
    if (rawType) {
      const geom = shape.map((v) => ({ x: v.x, y: v.z, z: v.y }));
      if (objectType === 'Meter') geom.pop();
      const newShape = {
        id: canvasHandler.uuid(),
        shape: shapeType,
        geometry: geom,
        objectType: objectType
      };
      handleNewShape(newShape, false);
    } else {
      const geometry = verticesToSegments([shape], true);
      const geometries = shapeType === 'Polygon' || shapeType === 'Line' ? splitGeometry(geometry[0]) : [geometry[0]];
      geometries.forEach((geo) => {
        const newShape = {
          id: canvasHandler.uuid(),
          shape: shapeType,
          geometry: geo
        };
        if (shapeType === 'Point') {
          newShape.geometry = [geometry[0][0]];
          newShape.radius = getInchePerSceneUnit(shape[0].clone().sub(shape[1]).length()).toFixed(2);
        }
        handleNewShape(newShape, false);
      });
    }
  };

  const editShapes = (shapes, objectType = null) => {
    let oldPolygons;
    if (accordion === 'segments' || accordion === 'obstacles') {
      oldPolygons = accordion === 'segments' ? segments : obstacles;
    } else if (accordion === 'trees') {
      oldPolygons = trees;
    } else if (accordion === 'annotations') {
      const newAnnotations = [...annotationList];
      newAnnotations
        .filter((a) => a.objectType == objectType)
        .forEach((a, k) => {
          a.geometry = shapes[k].map((v) => ({ x: v.x, y: v.z, z: v.y }));
        });
      setAnnotations(newAnnotations);
      return;
    } else return;

    for (let nsi = 0; nsi < shapes.length; nsi += 1) {
      const targetPolygon = oldPolygons?.[nsi];
      if (!targetPolygon) continue;
      const ns = shapes[nsi].map((p) => p.clone());
      const editedGeometry = verticesToSegments([ns], true)[0];
      switch (targetPolygon.shape) {
        case 'Polygon':
        case 'Line':
        case 'Circle': {
          const oldGeom = targetPolygon.geometry || [];
          const isChanged =
            oldGeom.length !== editedGeometry.length ||
            oldGeom.some(
              ([x, y], gi) =>
                !editedGeometry[gi] ||
                Math.abs(editedGeometry[gi][0] - x) > 1e-4 ||
                Math.abs(editedGeometry[gi][1] - y) > 1e-4
            );
          if (isChanged) {
            const editedShape = {
              ...targetPolygon,
              geometry: editedGeometry
            };
            handleSaveEdits(editedShape, false);
          }
          break;
        }
        case 'Point': {
          const ns0 = ns[0].clone();
          const ns1 = ns[1].clone();
          ns0.y = 0;
          ns1.y = 0;
          const newR = Math.max(10, getInchePerSceneUnit(ns0.sub(ns1).length()).toFixed(2));
          const oldR = targetPolygon.radius;
          const [newX, newY] = editedGeometry[0];
          const [oldX, oldY] = targetPolygon.geometry[0];
          if (Math.abs(newX - oldX) > 1e-4 || Math.abs(newY - oldY) > 1e-4 || newR !== oldR) {
            const editedShape = {
              ...targetPolygon,
              geometry: [[newX, newY]],
              radius: newR
            };
            handleSaveEdits(editedShape, false);
          }
          break;
        }
        default:
          break;
      }
    }
  };

  const deleteShape = (index, objectType = null) => {
    switch (accordion) {
      case 'segments':
        handleDeleteShape(segments[index].id);
        break;
      case 'obstacles':
        handleDeleteShape(obstacles[index].id);
        break;
      case 'trees':
        handleDeleteShape(trees[index].id);
        break;
      case 'annotations': {
        const newAnnotations = [...annotationList];
        const filteredAnnotation = newAnnotations.filter((a) => a.objectType == objectType);
        const idx = newAnnotations.findIndex((a) => a == filteredAnnotation[index]);
        if (idx > -1) {
          newAnnotations.splice(idx, 1);
          setAnnotations(newAnnotations);
        }
        break;
      }
      default:
        break;
    }
  };

  const deletePointFromShape = (shapeIndex, vertexIndex, objectType = null) => {
    switch (accordion) {
      case 'segments': {
        const count = segments[shapeIndex].geometry.length;
        const vindex = changeClockwise[shapeIndex] ? (count - vertexIndex) % count : vertexIndex;
        handleRemovePointFromPolygon(shapeIndex, vindex);
        break;
      }
      case 'obstacles': {
        const obstacleIndex = segments.length + shapeIndex;
        const count = obstacles[shapeIndex].geometry.length;
        const vindex = changeClockwise[obstacleIndex] ? (count - vertexIndex) % count : vertexIndex;
        handleRemovePointFromPolygon(shapeIndex, vindex);
        break;
      }
      case 'annotations': {
        const newAnnotations = [...annotationList];
        const filteredAnnotation = newAnnotations.filter((a) => a.objectType == objectType);
        const idx = newAnnotations.findIndex((a) => a == filteredAnnotation[shapeIndex]);
        if (idx > -1) {
          const ann = newAnnotations[idx];
          ann.geometry.splice(vertexIndex, 1);
          const isLine = ann.shape == 'Line' || ann.shape === 'line' || ann.tool === 'Line';
          const isPolygon = ann.shape == 'Polygon' || ann.shape === 'polygon' || ann.tool === 'Polygon';
          if ((isLine && ann.geometry.length < 2) || (isPolygon && ann.geometry.length < 3)) {
            newAnnotations.splice(idx, 1);
          }
          setAnnotations(newAnnotations);
        }
        break;
      }
      default:
        break;
    }
  };

  const addPointToShape = (shapeIndex, lineIndex, point, objectType = null) => {
    switch (accordion) {
      case 'segments': {
        const count = segments[shapeIndex].geometry.length;
        const vindex = changeClockwise[shapeIndex] ? (count - lineIndex) % count : lineIndex - 1;
        const newPoint = pointToSegments(point, true);
        handleAddPointToPolygon(shapeIndex, vindex, newPoint, true);
        break;
      }
      case 'obstacles': {
        const obstacleIndex = segments.length + shapeIndex;
        const count = obstacles[shapeIndex].geometry.length;
        const vindex = changeClockwise[obstacleIndex] ? (count - lineIndex) % count : lineIndex - 1;
        const newPoint = pointToSegments(point, true);
        handleAddPointToPolygon(shapeIndex, vindex, newPoint, true);
        break;
      }
      case 'annotations': {
        const newAnnotations = [...annotationList];
        const filteredAnnotation = newAnnotations.filter((a) => a.objectType == objectType);
        const idx = newAnnotations.findIndex((a) => a == filteredAnnotation[shapeIndex]);
        if (idx > -1) {
          const newPoint = { x: point.x, y: point.z, z: point.y };
          newAnnotations[idx].geometry = [
            ...newAnnotations[idx].geometry.slice(0, lineIndex),
            newPoint,
            ...newAnnotations[idx].geometry.slice(lineIndex)
          ];
          setAnnotations(newAnnotations);
        }
        break;
      }
      default:
        break;
    }
  };

  const sourcePolygons = () => {
    if (accordion === 'segments') return segments;
    if (accordion === 'obstacles') return obstacles;
    return null;
  };

  const ccwRingAndLabels = (shape) => {
    const geometry = Array.isArray(shape?.geometry) ? shape.geometry : [];
    const rawLines = Array.isArray(shape?.lines) ? shape.lines : Array(geometry.length).fill('');
    const rawSetbacks = Array.isArray(shape?.setbacks) ? shape.setbacks : Array(geometry.length).fill(0);
    const [ccwGeo, ccwLines, clockwise] = counterClockWise(geometry, rawLines);
    const setbacks = clockwise ? [...rawSetbacks].reverse() : [...rawSetbacks];
    return {
      ring: ccwGeo.map((p) => ({ x: Number(p[0]), y: Number(p[1]) })),
      lines: ccwLines,
      setbacks
    };
  };

  const geometryWithLabels = (scenePoints, sources) => {
    const geometry = verticesToSegments([scenePoints], true)[0];
    if (!geometry || geometry.length < 3) return null;
    const geomRing = geometry.map((p) => ({ x: p[0], y: p[1] }));
    return {
      geometry,
      lines: mapRingEdgeLabels(
        geomRing,
        sources.map((src) => ({ ring: src.ring, lines: src.lines }))
      ),
      setbacks: mapRingEdgeLabels(
        geomRing,
        sources.map((src) => ({ ring: src.ring, lines: src.setbacks }))
      ).map((value) => {
        const next = Number(value);
        return Number.isFinite(next) ? next : 0;
      })
    };
  };

  const mergeShapes = (indexA, indexB, mergedPlanar, objectType = null) => {
    if (indexA === indexB || indexA < 0 || indexB < 0) return;
    const scenePoints = sceneFromPlanar(mergedPlanar);
    if (accordion === 'annotations') {
      const filtered = annotationList.filter((a) => a.objectType == objectType);
      const first = filtered[indexA];
      const second = filtered[indexB];
      if (!first || !second) return;
      const created = {
        ...first,
        id: canvasHandler.uuid(),
        geometry: scenePoints.map((v) => ({ x: v.x, y: v.z, z: v.y }))
      };
      setAnnotations(
        annotationList.filter((item) => item !== first && item !== second).concat(created)
      );
      return;
    }
    const source = sourcePolygons();
    const first = source?.[indexA];
    const second = source?.[indexB];
    if (!first || !second) return;
    const labeled = geometryWithLabels(scenePoints, [ccwRingAndLabels(first), ccwRingAndLabels(second)]);
    if (!labeled) return;
    const simplified = simplifyCollinearRing(labeled.geometry, labeled.lines, [labeled.setbacks]);
    const plane = averagePlaneAttributes([first, second], [polygonArea(first.geometry), polygonArea(second.geometry)]);
    const created = {
      ...first,
      id: canvasHandler.uuid(),
      shape: first.shape || 'Polygon',
      geometry: simplified.ring.map((p) => [p.x, p.y]),
      lines: simplified.lines,
      setbacks: simplified.extras[0] || labeled.setbacks,
      panelAxis: null,
      ...plane
    };
    if (typeof handleReplaceShapes === 'function') {
      handleReplaceShapes(created, [first.id, second.id], false);
      return;
    }
    handleNewShape(created, false);
    handleDeleteShape(first.id);
    handleDeleteShape(second.id);
  };

  const splitShapes = (index, partsPlanar, objectType = null) => {
    if (!Array.isArray(partsPlanar) || partsPlanar.length < 2) return;
    const [firstPart, secondPart] = partsPlanar;
    const firstScene = sceneFromPlanar(firstPart);
    const secondScene = sceneFromPlanar(secondPart);
    if (accordion === 'annotations') {
      const filtered = annotationList.filter((a) => a.objectType == objectType);
      const current = filtered[index];
      if (!current) return;
      const firstGeom = firstScene.map((v) => ({ x: v.x, y: v.z, z: v.y }));
      const secondGeom = secondScene.map((v) => ({ x: v.x, y: v.z, z: v.y }));
      setAnnotations(
        annotationList.flatMap((item) => {
          if (item !== current) return [item];
          return [
            { ...item, id: canvasHandler.uuid(), geometry: firstGeom },
            { ...item, id: canvasHandler.uuid(), geometry: secondGeom }
          ];
        })
      );
      return;
    }
    const source = sourcePolygons();
    const current = source?.[index];
    if (!current) return;
    const sourceLabels = [ccwRingAndLabels(current)];
    const firstLabeled = geometryWithLabels(firstScene, sourceLabels);
    const secondLabeled = geometryWithLabels(secondScene, sourceLabels);
    if (!firstLabeled || !secondLabeled) return;
    const firstCreated = {
      ...current,
      id: canvasHandler.uuid(),
      shape: current.shape || 'Polygon',
      geometry: firstLabeled.geometry,
      lines: firstLabeled.lines,
      setbacks: firstLabeled.setbacks,
      panelAxis: null
    };
    const secondCreated = {
      ...current,
      id: canvasHandler.uuid(),
      shape: current.shape || 'Polygon',
      geometry: secondLabeled.geometry,
      lines: secondLabeled.lines,
      setbacks: secondLabeled.setbacks,
      panelAxis: null
    };
    if (typeof handleReplaceShapes === 'function') {
      handleReplaceShapes([firstCreated, secondCreated], [current.id], false);
      return;
    }
    handleNewShape(firstCreated, false);
    handleNewShape(secondCreated, false);
    handleDeleteShape(current.id);
  };

  const setLineLabel = (shapeIndex, lineIndex, label, toggle = true) => {
    if (accordion === 'segments') {
      const count = segments[shapeIndex].geometry.length;
      const vindex = changeClockwise[shapeIndex] ? (count - lineIndex) % count : (count + lineIndex - 1) % count;
      const lines = [...(segments[shapeIndex].lines || Array(count).fill(''))];
      if (lines[vindex] === label && toggle) lines[vindex] = '';
      else lines[vindex] = label;
      handleLineLabel(shapeIndex, vindex, lines);
    }
  };

  return {
    pointToSegments,
    verticesToSegments,
    addShape,
    editShapes,
    mergeShapes,
    splitShapes,
    deleteShape,
    deletePointFromShape,
    addPointToShape,
    setLineLabel
  };
};

export default useDrawing;
